import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert, Spinner, Stack } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Stepper from '../../../components/stepper/Stepper';
import { useGetStripeKeyQuery } from '../../../services/endpoints/finance/stripe';
import { Discount, LocationOrders, OrderPlusCost, Scholarship } from '../../../types/orders.type';
import { SFCLocation } from '../../../types/place.types';
import { OrderComplete, OrderReview } from './checkoutSteps';

const steps = [
  { name: 'Review/Payment', form: OrderReview },
  { name: 'Complete Registration', form: OrderComplete }
];

export interface FormValues {
  id?: number;
  location: Pick<SFCLocation, 'id' | 'title'>;
  orders: OrderPlusCost[];
  discounts: Discount[];
  scholarships: Scholarship[];
  promoCodeSearch?: string;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values: FormValues;
  setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<FormValues>>;
  next: () => void;
}

function getValues(obl: LocationOrders, discounts: Discount[]): FormValues {
  return {
    ...obl,
    discounts,
    scholarships: []
  };
}

interface OrderCheckoutProps {
  locationOrders: LocationOrders;
  discounts: Discount[];
}

const OrderCheckout: React.FC<OrderCheckoutProps> = ({ locationOrders: obl, discounts }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [searchParams] = useSearchParams();

  const initialValues = getValues(obl, discounts);
  const locationId = obl.location.id;
  const complete_qs = searchParams.get('complete');

  const {
    data: StripeData,
    error: StripeError,
    isLoading: StripeLoading
  } = useGetStripeKeyQuery(locationId);

  // Load Stripe.js once we have the publishable key.
  useEffect(() => {
    if (StripeData) {
      setStripePromise(loadStripe(StripeData.publishableKey));
    }
  }, [StripeData]);

  // Advance to OrderComplete when redirected back from Stripe with ?complete=.
  // Checkout.tsx sets this query param after a successful PaymentIntent + order update.
  useEffect(() => {
    if (complete_qs && complete_qs === String(locationId)) {
      setCurrentStep(steps.length - 1);
    }
  }, [complete_qs, locationId]);

  // Buttons rendered on the final (OrderComplete) step.
  const finalStepButtons = (
      <div className="order-complete-actions w-100">
        <Stack
            direction="horizontal"
            gap={2}
            className="flex-column flex-sm-row"
        >
          <a
              href="/user/orders"
              className="btn btn-primary mt-3 w-100"
          >
            View Order Details
          </a>
          <a
              href="/"
              className="btn btn-outline-primary mt-3 w-100"
          >
            Back to SFC Home
          </a>
        </Stack>
      </div>
  );

  return (
      <div className="w-100">
        <div className="w-100 d-flex flex-column flex-sm-row justify-content-center align-items-center">
          <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={false}
              onStepClick={(step) => setCurrentStep(step)}
          />
        </div>
        <div className="w-100 d-flex justify-content-center align-items-center pt-3">
          <Stack gap={1} className="checkout-body">
            {StripeError ? (
                <Alert variant="warning" className="mt-3">
                  Online payments aren't set up for this location yet. Please contact
                  Sticky Fingers Cooking directly to complete your registration.
                </Alert>
            ) : StripeLoading || !stripePromise ? (
                <div
                    style={{ minHeight: 'calc(20vh + 140px)' }}
                    className="d-flex align-items-center justify-content-center mt-4"
                >
                  <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <Elements
                    stripe={stripePromise}
                    options={{ mode: 'payment', amount: 1000, currency: 'usd' }}
                >
                  <Formik
                      initialValues={initialValues}
                      onSubmit={(form) => {}}
                      enableReinitialize
                  >
                    {({ touched, errors, values, setFieldValue }: FormikProps<FormValues>) => (
                        <FormikForm className="mt-3 text-start">
                          <div style={{ minHeight: 'calc(20vh + 140px)' }}>
                            {currentStep < steps.length &&
                                React.createElement(steps[currentStep]?.form, {
                                  errors,
                                  touched,
                                  values,
                                  setFieldValue,
                                  next: () => setCurrentStep(currentStep + 1)
                                })}
                          </div>
                          {currentStep === steps.length - 1 && finalStepButtons}
                        </FormikForm>
                    )}
                  </Formik>
                </Elements>
            )}
          </Stack>
        </div>
      </div>
  );
};

export default OrderCheckout;